import {BaseAttribute, BaseAttributeOptions} from './BaseAttribute';
import DecimalUtils from '@hit/components/src/utils/number/decimal';

/** Options for decimal attribute = All options from BaseAttribute + decimal related options */
export type DecimalAttributeOptions = BaseAttributeOptions & {
  decimalPrecision?: number; // How many decimal places to display in the UI
  decimalKeepNullValue?: boolean; // Keep NULL as NULL value or convert it to 0 in decimal format
  decimalPrecisionForcedByColumn?: string | null; // Define column used to define the decimal precision
};

/** Class definition for decimal attributes */
export class DecimalAttribute extends BaseAttribute {
  decimalPrecision: number;
  decimalKeepNullValue: boolean;
  decimalPrecisionForcedByColumn?: string | null;

  constructor(options: DecimalAttributeOptions = {}) {
    const {
      decimalPrecision = 4,
      decimalKeepNullValue = false,
      decimalPrecisionForcedByColumn = null,
      ...baseOptions
    } = options;
    super(baseOptions);
    this.decimalPrecision = decimalPrecision;
    this.decimalKeepNullValue = decimalKeepNullValue;
    this.decimalPrecisionForcedByColumn = decimalPrecisionForcedByColumn;
  }

  // Input type that is used if attribute is used in edit mode
  get inputType(): string {
    return 'decimal';
  }

  /** We use the same option from the base attribute but we convert it into decimal format */
  getInitialValue(): number | string | undefined {
    if (typeof this.creationValue === 'string') {
      return parseFloat(this.creationValue).toFixed(this.decimalPrecision);
    } else if (typeof this.creationValue === 'number') {
      return this.creationValue.toFixed(this.decimalPrecision);
    }
    return undefined;
  }

  /**
   * Value is converted into decimal format
   * Returns two values:
   * - The value sent to the database (converted into decimal format)
   * - The value to update the data in the UI (converted into decimal format)
   */
  toDbValue(value: unknown): [unknown, unknown] {
    const decimalValue = DecimalUtils.parseDecimal(
      value,
      this.decimalPrecision,
      this.getInitialValue() as number | undefined,
      this.decimalKeepNullValue,
    );
    return [decimalValue, decimalValue];
  }

  /** Converts the value from the database to a value that can be used in the UI */
  toLocalValue(item: Record<string, unknown>): unknown {
    let value = item[this.name];
    if ((value === undefined || value === null) && this.column) {
      value = item[this.column];
    }
    if (this.transform) {
      value = this.transform(value);
    }
    if (!value) {
      if (this.decimalKeepNullValue) return null;
      value = 0;
    }
    return DecimalUtils.parseDecimal(
      value,
      this.decimalPrecision,
      this.getInitialValue() as number | undefined,
      this.decimalKeepNullValue,
    );
  }
}
